import type { TCustomEndpointInput } from '@homepal/api-contract'

/**
 * Split what was pasted into the "add several" field into endpoint names.
 * Newlines and commas both separate; surrounding whitespace is dropped, as
 * are empty entries and repeats (first spelling wins).
 */
export const parseEndpointNames = (text: string): string[] => {
  const seen = new Set<string>()
  const names: string[] = []

  text.split(/[\n,]/).forEach((part) => {
    const name = part.trim()

    if (!name || seen.has(name)) {
      return
    }

    seen.add(name)
    names.push(name)
  })

  return names
}

/**
 * Remove `affix` from the start or the end of a name, whichever matches
 * (the start wins if both do). Views are often exported with a marker like
 * HOMEPAL_ that has no business in the Homepal table name. A name that is
 * nothing but the affix is left alone rather than turned into ''.
 */
export const stripAffix = (name: string, affix: string): string => {
  if (!affix) {
    return name
  }

  let result = name

  if (result.startsWith(affix)) {
    result = result.slice(affix.length)
  } else if (result.endsWith(affix)) {
    result = result.slice(0, -affix.length)
  }

  return result || name
}

/**
 * The preview rows for a pasted list: the technical name as pasted, and a
 * suggested table name with the affix stripped. Both are edited by the user
 * before anything is sent.
 */
export const suggestEndpoints = (
  text: string,
  affix: string
): TCustomEndpointInput[] =>
  parseEndpointNames(text).map((name) => ({
    technical_name: name,
    table_name: stripAffix(name, affix.trim())
  }))
